/**
Author - Venkatesh Pullaganti😃
Git - https://github.com/venkatesh1146
**/

import {useRef, useCallback} from 'react';
import {getAAConsentStatus} from 'api';
import {handleApiError, triggerToast} from 'helpers';
import {AA_POLLING_INTERVAL, AA_MAX_POLLING_ATTEMPTS} from 'constants/AAConstants';
import {isConsentComplete, isConsentFailed} from '../services/consentService';
import {useAADispatch} from '../context/AAContext';
import {ACTION_TYPES} from '../context/AAContext';

const useConsentPolling = (updateStatus) => {
  const dispatch = useAADispatch();
  const pollingIntervalRef = useRef(null);
  const pollingAttemptsRef = useRef(0);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    pollingAttemptsRef.current = 0;
  }, []);

  const startPolling = useCallback(
    (consentId) => {
      pollingAttemptsRef.current = 0;

      pollingIntervalRef.current = setInterval(async () => {
        try {
          pollingAttemptsRef.current += 1;

          if (pollingAttemptsRef.current >= AA_MAX_POLLING_ATTEMPTS) {
            stopPolling();
            triggerToast('Verification timeout. Please try again or upload documents.');
            dispatch({type: ACTION_TYPES.SET_SHOW_UPLOAD_OPTION, payload: true});
            return;
          }

          const response = await getAAConsentStatus(consentId);
          const statusData = response.data;

          dispatch({
            type: ACTION_TYPES.SET_CONSENT_DATA,
            payload: {
              status: statusData.status,
              processingStatus: statusData.wConsentStatus,
              errorMessage: statusData.errors,
            },
          });

          if (isConsentFailed(statusData.status, statusData.wConsentStatus)) {
            stopPolling();
            dispatch({type: ACTION_TYPES.SET_SHOW_UPLOAD_OPTION, payload: true});
          } else if (isConsentComplete(statusData.wConsentStatus)) {
            stopPolling();
            await updateStatus();
          }
        } catch (error) {
          const errorData = error?.response?.data;
          if (errorData?.detail?.code === 'create_new') {
            stopPolling();
            dispatch({
              type: ACTION_TYPES.SET_ERROR,
              payload: {errorCode: 'create_new'},
            });
            dispatch({type: ACTION_TYPES.SET_SHOW_UPLOAD_OPTION, payload: true});
          } else {
            handleApiError(error);
            stopPolling();
            dispatch({type: ACTION_TYPES.SET_SHOW_UPLOAD_OPTION, payload: true});
          }
        }
      }, AA_POLLING_INTERVAL);
    },
    [stopPolling, updateStatus, dispatch]
  );

  return {
    startPolling,
    stopPolling,
    pollingIntervalRef,
  };
};

export default useConsentPolling;
